import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';

// ─── New staff modal ───────────────────────────────────────────────────────────
function StaffModal({ workCentres, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', role: '', pin: '', work_centre_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return setError('Name is required');
    if (!/^\d{4}$/.test(form.pin)) return setError('PIN must be exactly 4 digits');
    setSaving(true);
    setError(null);
    try {
      await api.post('/staff', {
        name: form.name.trim(),
        role: form.role.trim() || null,
        pin: form.pin,
        work_centre_id: form.work_centre_id ? parseInt(form.work_centre_id) : null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">New Staff Member</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label>Role</label>
            <input value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. Production" />
          </div>
          <div className="form-group">
            <label>PIN * (4 digits)</label>
            <input
              value={form.pin}
              onChange={e => set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric" maxLength={4} placeholder="1234"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </div>
          <div className="form-group">
            <label>Work Centre</label>
            <select value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
              <option value="">None</option>
              {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Create Staff Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Staff row — inline edit ────────────────────────────────────────────────────
function StaffRow({ s, workCentres, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [form, setForm] = useState({ name: s.name, pin: s.pin || '', work_centre_id: s.work_centre_id || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const startEdit = () => {
    setForm({ name: s.name, pin: s.pin || '', work_centre_id: s.work_centre_id || '' });
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!form.name.trim()) return setError('Name is required');
    if (form.pin && !/^\d{4}$/.test(form.pin)) return setError('PIN must be exactly 4 digits');
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/staff/${s.id}`, {
        name: form.name.trim(),
        pin: form.pin || null,
        work_centre_id: form.work_centre_id ? parseInt(form.work_centre_id) : null,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setSaving(false);
  };

  return (
    <tr>
      <td>
        {editing
          ? <input value={form.name} onChange={e => set('name', e.target.value)} style={{ width: '100%' }} />
          : <span onClick={startEdit} style={{ cursor: 'pointer', fontWeight: 600 }}>{s.name}</span>}
      </td>
      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{s.role || '—'}</td>
      <td>
        {editing ? (
          <input
            value={form.pin}
            onChange={e => set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputMode="numeric" maxLength={4} placeholder="4 digits"
            style={{ width: 90, fontFamily: 'var(--font-mono)' }}
          />
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span onClick={startEdit} style={{ cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>
              {s.pin ? (revealed ? s.pin : '••••') : '—'}
            </span>
            {s.pin && (
              <button className="btn btn-secondary btn-sm" onClick={() => setRevealed(r => !r)}>
                {revealed ? 'Hide' : 'Show'}
              </button>
            )}
          </span>
        )}
      </td>
      <td>
        {editing ? (
          <select value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
            <option value="">None</option>
            {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
          </select>
        ) : (s.work_centre_name || '—')}
      </td>
      <td>
        {editing ? (
          <div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
            {error && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4, maxWidth: 180 }}>{error}</div>}
          </div>
        ) : (
          <button className="btn btn-secondary btn-sm" onClick={startEdit}>Edit</button>
        )}
      </td>
    </tr>
  );
}

// ─── Staff page ─────────────────────────────────────────────────────────────────
export default function Staff() {
  const [staff,       setStaff]       = useState([]);
  const [workCentres, setWorkCentres] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showNew,     setShowNew]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/staff/all'),
      api.get('/work-centres'),
    ]).then(([s, wc]) => {
      setStaff(s.data.value || []);
      setWorkCentres((wc.data.value || []).filter(w => w.is_active));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">STA<span>FF</span></h2>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Staff Member</button>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">Loading staff...</div>
        ) : staff.length === 0 ? (
          <div className="empty-state">No staff members</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>PIN</th>
                  <th>Work Centre</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {staff.map(s => (
                  <StaffRow key={s.id} s={s} workCentres={workCentres} onSaved={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && (
        <StaffModal
          workCentres={workCentres}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}
